import { useState, useCallback, type CSSProperties } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  BookAppointmentDrawer,
  type BookDrawerPrefill,
  type BookedAppointment,
} from '../pages/appointments/BookAppointmentDrawer';
import {
  useBrandTokens,
  useSidebarState,
  useResizableSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
  TopBar,
  NavIcon,
  ContactRow,
  ResizeGutter,
  SidebarToggleButton,
  ClinicModePills,
  orgName,
  orgLogoUrl,
  logoMarkImgStyle,
  orgLogoHorizontalUrl,
  sidebarLogoMaxWidth,
  sidebarLogoMaxHeight,
  sidebarLogoObjectPosition,
  sidebarLogoMargin,
} from './shared';
import { useClinicContact } from '../hooks/useClinicContact';
import { useVisibleNav } from '../auth/useVisibleNav';
import { usePinnedRoutes, ROUTE_TO_PIN_KEY } from './usePinnedRoutes';

// ─── Appointments Sidebar Nav ───

const APPOINTMENTS_NAV = [
  {
    label: 'Book a Patient',
    subtitle: 'Create an appointment for a patient',
    icon: 'plus',
    path: '/appointments/new',
  },
] as const;

const VIEW_NAV = [
  { label: 'Month', icon: 'calendar', path: '/appointments/month' },
  { label: 'Week', icon: 'calendar', path: '/appointments/week' },
  { label: 'Day', icon: 'calendar', path: '/appointments/day' },
  { label: 'List', icon: 'list', path: '/appointments/list' },
] as const;

const SERVICES_NAV = [
  { label: 'Manage Services', icon: 'clipboard', path: '/appointments/services' },
] as const;

const PROVIDERS_NAV = [
  { label: 'Manage Providers', icon: 'stethoscope', path: '/appointments/providers' },
  {
    label: 'Provider Availability',
    icon: 'calendar',
    path: '/appointments/provider-availability',
  },
] as const;

const SETTINGS_NAV = [
  { label: 'Calendar', icon: 'calendar', path: '/appointments/settings/calendar' },
  { label: 'Category & Statuses', icon: 'clipboard', path: '/appointments/settings/categories' },
  { label: 'Online Booking', icon: 'credit-card', path: '/appointments/settings/online-booking' },
  {
    label: 'Front Desk Booking',
    icon: 'clipboard',
    path: '/appointments/settings/front-desk-booking',
  },
] as const;

// ─── Sidebar Nav Item ───

function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={pinned ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: 'rotate(45deg)' }}
    >
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

function SidebarNavItem({
  item,
  active,
  navigate,
  pinned,
  onTogglePin,
}: {
  item: { label: string; subtitle?: string; icon: string; path: string };
  active: boolean;
  navigate: (path: string) => void;
  pinned?: boolean;
  onTogglePin?: () => void;
}) {
  const hasSubtitle = !!item.subtitle;
  return (
    <div
      className="nav-pin-row"
      style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
    >
      <a
        href={item.path}
        onClick={(e) => {
          e.preventDefault();
          navigate(item.path);
        }}
        style={{
          display: 'flex',
          alignItems: hasSubtitle ? 'flex-start' : 'center',
          gap: 'var(--sp-3)',
          minHeight: active ? 48 : 44,
          padding: active
            ? hasSubtitle
              ? '10px 18px'
              : '0 18px'
            : hasSubtitle
              ? '10px 16px'
              : '0 16px',
          borderRadius: 'var(--radius-md)',
          background: active ? 'var(--gradient-nav-active)' : 'transparent',
          border: active ? '2px solid var(--color-primary-contrast)' : '2px solid transparent',
          color: active ? 'var(--text)' : 'var(--text-muted)',
          textDecoration: 'none',
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-medium)',
          lineHeight: 'var(--line-height-base)',
          letterSpacing: '-0.15px',
          cursor: 'pointer',
          flex: 1,
        }}
      >
        {hasSubtitle ? (
          <span style={{ marginTop: 2, flexShrink: 0, display: 'flex' }}>
            <NavIcon name={item.icon} />
          </span>
        ) : (
          <NavIcon name={item.icon} />
        )}
        {hasSubtitle ? (
          <div>
            <div>{item.label}</div>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                fontWeight: 'var(--font-weight-normal)',
                lineHeight: 'var(--line-height-xs)',
                marginTop: 1,
              }}
            >
              {item.subtitle}
            </div>
          </div>
        ) : (
          item.label
        )}
      </a>
      {onTogglePin && (
        <button
          onClick={onTogglePin}
          className="pin-btn"
          style={{
            position: 'absolute',
            right: 8,
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: pinned ? 'var(--color-primary)' : 'var(--text-muted)',
            cursor: 'pointer',
            opacity: pinned ? 1 : 0,
            transition: 'opacity 150ms ease',
            padding: 0,
          }}
        >
          <PinIcon pinned={!!pinned} />
        </button>
      )}
    </div>
  );
}

// ─── Section Label ───

function SectionLabel({ label, badge }: { label: string; badge?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          lineHeight: 'var(--line-height-sm)',
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {badge && (
        <span
          style={{
            height: 19,
            padding: '0 var(--sp-2)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--color-warning-bg)',
            color: 'var(--color-warning-text)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: '19px',
            letterSpacing: '0.12px',
          }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

// ─── Divider ───

function Divider() {
  return (
    <div
      style={{
        height: 1,
        background: 'var(--border)',
        margin: 'var(--sp-3) 0',
      }}
    />
  );
}

// ─── Appointments Sidebar ───

function AppointmentsSidebar({
  onCollapse,
  onBookPatient,
  isPinned,
  togglePinned,
}: {
  onCollapse: () => void;
  onBookPatient: () => void;
  isPinned: (key: string) => boolean;
  togglePinned: (key: string) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleAppointments = useVisibleNav(APPOINTMENTS_NAV);
  const visibleView = useVisibleNav(VIEW_NAV);
  const visibleServices = useVisibleNav(SERVICES_NAV);
  const visibleProviders = useVisibleNav(PROVIDERS_NAV);
  const visibleSettings = useVisibleNav(SETTINGS_NAV);

  const clinic = useClinicContact();
  return (
    <aside
      className="shell-sidebar pc-sidebar"
      style={{
        width: '100%',
        height: '100%',
        overflowX: 'clip',
        overflowY: 'auto',
        transition: 'width 200ms ease-out',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
      }}
    >
      <div style={{ width: '100%', height: '100%' } as React.CSSProperties}>
        {/* Clinic Info Header */}
        <div
          style={{
            padding: 'var(--sp-6)',
            borderBottom: '1px solid var(--border)',
            position: 'relative',
          }}
        >
          {/* Collapse control. The collapsed RAIL expands on click, but there
              was no way back the other way except dragging the gutter. */}
          <div style={{ position: 'absolute', top: 'var(--sp-3)', right: 'var(--sp-3)' }}>
            <SidebarToggleButton collapsed={false} onToggle={onCollapse} />
          </div>
          {orgLogoHorizontalUrl ? (
            <img
              src={orgLogoHorizontalUrl}
              alt={orgName}
              style={{
                width: '100%',
                maxWidth: sidebarLogoMaxWidth,
                maxHeight: sidebarLogoMaxHeight,
                display: 'block',
                margin: sidebarLogoMargin,
                objectFit: 'contain',
                objectPosition: sidebarLogoObjectPosition,
              }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              {orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  style={{ ...logoMarkImgStyle(40), flexShrink: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                  }}
                />
              )}
              <div>
                <span
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-semi)',
                    lineHeight: 'var(--line-height-lg)',
                    letterSpacing: '-0.44px',
                    color: 'var(--text)',
                    display: 'block',
                  }}
                >
                  {orgName}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Helios MedGroup
                </span>
              </div>
            </div>
          )}

          {/* Contact rows */}
          <div
            style={{
              marginTop: 'var(--sp-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            {clinic.address && <ContactRow icon="map-pin" text={clinic.address} />}
            {clinic.phone && <ContactRow icon="phone" text={clinic.phone} />}
            {clinic.email && <ContactRow icon="mail" text={clinic.email} />}
          </div>
        </div>

        <ClinicModePills />

        {/* Navigation Sections */}
        <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
          {/* APPOINTMENTS Section */}
          {visibleAppointments.length > 0 && (
            <>
              <SectionLabel label="Appointments" />
              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {visibleAppointments.map((item) => (
                  <SidebarNavItem
                    key={item.label}
                    item={item}
                    active={false}
                    navigate={() => onBookPatient()}
                  />
                ))}
              </nav>
            </>
          )}

          {/* VIEW Section */}
          {visibleView.length > 0 && (
            <>
              {visibleAppointments.length > 0 && <Divider />}
              <SectionLabel label="View" />
              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {visibleView.map((item) => {
                  const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                  return (
                    <SidebarNavItem
                      key={item.label}
                      item={item}
                      active={location.pathname === item.path}
                      navigate={navigate}
                      pinned={pinKey ? isPinned(pinKey) : undefined}
                      onTogglePin={pinKey ? () => togglePinned(pinKey) : undefined}
                    />
                  );
                })}
              </nav>
            </>
          )}

          {/* SERVICES Section */}
          {visibleServices.length > 0 && (
            <>
              {(visibleAppointments.length > 0 || visibleView.length > 0) && <Divider />}
              <SectionLabel label="Services" badge="Admin" />
              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {visibleServices.map((item) => {
                  const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                  return (
                    <SidebarNavItem
                      key={item.label}
                      item={item}
                      active={location.pathname === item.path}
                      navigate={navigate}
                      pinned={pinKey ? isPinned(pinKey) : undefined}
                      onTogglePin={pinKey ? () => togglePinned(pinKey) : undefined}
                    />
                  );
                })}
              </nav>
            </>
          )}

          {/* PROVIDERS Section */}
          {visibleProviders.length > 0 && (
            <>
              {(visibleAppointments.length > 0 ||
                visibleView.length > 0 ||
                visibleServices.length > 0) && <Divider />}
              <SectionLabel label="Providers" badge="Admin" />
              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {visibleProviders.map((item) => {
                  const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                  return (
                    <SidebarNavItem
                      key={item.label}
                      item={item}
                      active={location.pathname === item.path}
                      navigate={navigate}
                      pinned={pinKey ? isPinned(pinKey) : undefined}
                      onTogglePin={pinKey ? () => togglePinned(pinKey) : undefined}
                    />
                  );
                })}
              </nav>
            </>
          )}

          {/* SETTINGS Section */}
          {visibleSettings.length > 0 && (
            <>
              {(visibleAppointments.length > 0 ||
                visibleView.length > 0 ||
                visibleServices.length > 0 ||
                visibleProviders.length > 0) && <Divider />}
              <SectionLabel label="Settings" badge="Admin" />
              <nav
                style={{
                  marginTop: 'var(--sp-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                  paddingBottom: 'var(--sp-6)',
                }}
              >
                {visibleSettings.map((item) => {
                  const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                  return (
                    <SidebarNavItem
                      key={item.label}
                      item={item}
                      active={location.pathname === item.path}
                      navigate={navigate}
                      pinned={pinKey ? isPinned(pinKey) : undefined}
                      onTogglePin={pinKey ? () => togglePinned(pinKey) : undefined}
                    />
                  );
                })}
              </nav>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}

// ─── Appointments Sidebar Rail (collapsed) ───
//
// Mirrors the Inventory/Queue rails: when the sidebar is collapsed, render
// a clean narrow rail with stacked icon buttons instead of clipping the
// full panel (which would otherwise leak rotated nav labels). One icon
// per primary destination — the calendar Day view, Services, Providers,
// and a Settings entry point (linking to Calendar settings as the
// canonical first settings page).

const RAIL_NAV: ReadonlyArray<{ label: string; icon: string; path: string }> = [
  { label: 'Day View', icon: 'calendar', path: '/appointments/day' },
  { label: 'Manage Services', icon: 'clipboard', path: '/appointments/services' },
  { label: 'Manage Providers', icon: 'stethoscope', path: '/appointments/providers' },
  { label: 'Settings', icon: 'gear', path: '/appointments/settings/calendar' },
];

function AppointmentsSidebarRail({ onExpand }: { onExpand: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleRail = useVisibleNav(RAIL_NAV);

  return (
    <aside
      className="shell-sidebar pc-sidebar"
      onClick={onExpand}
      title="Expand sidebar"
      style={{
        width: SIDEBAR_COLLAPSED_WIDTH,
        height: '100%',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 'var(--sp-4)',
        gap: 'var(--sp-1)',
        cursor: 'pointer',
      }}
    >
      {orgLogoUrl ? (
        <img
          src={orgLogoUrl}
          alt={orgName}
          style={{ ...logoMarkImgStyle(32), flexShrink: 0, marginBottom: 'var(--sp-3)' }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
            flexShrink: 0,
            marginBottom: 'var(--sp-3)',
          }}
        />
      )}

      {visibleRail.map((item) => {
        // For Settings, treat any /appointments/settings/* route as active
        // so the highlight survives on sub-pages.
        const active = item.path.startsWith('/appointments/settings')
          ? location.pathname.startsWith('/appointments/settings')
          : location.pathname === item.path;
        return (
          <button
            key={item.path}
            title={item.label}
            onClick={(e) => {
              e.stopPropagation();
              navigate(item.path);
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: active ? 'var(--gradient-nav-active)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              padding: 0,
            }}
          >
            <NavIcon name={item.icon} />
          </button>
        );
      })}
    </aside>
  );
}

// ─── Main Layout ───

// ─── Outlet context type ───

export interface AppointmentsOutletContext {
  collapsed: boolean;
  openBookDrawer: (prefill?: BookDrawerPrefill) => void;
  onToggleSidebar?: () => void;
}

export function useAppointmentsContext() {
  return useOutletContext<AppointmentsOutletContext>();
}

// ─── Layout ───

export function AppointmentsShellLayout() {
  const { collapsed, toggle } = useSidebarState('powercareai:appointments-sidebar-collapsed');
  const {
    width: sbWidth,
    dragging,
    startDrag,
    resetWidth,
  } = useResizableSidebar({
    storageKey: 'powercareai:appointments-sidebar-width',
    min: 280,
    max: 520,
    defaultWidth: 320,
    collapsed,
    onExpand: toggle,
  });
  const brandTokens = useBrandTokens();
  const { isPinned, togglePinned } = usePinnedRoutes();

  // Book Appointment Drawer state
  const [bookOpen, setBookOpen] = useState(false);
  const [bookPrefill, setBookPrefill] = useState<BookDrawerPrefill | undefined>();
  const [, setBookedList] = useState<BookedAppointment[]>([]);

  const openBookDrawer = useCallback((prefill?: BookDrawerPrefill) => {
    setBookPrefill(prefill);
    setBookOpen(true);
  }, []);

  const handleBooked = useCallback((appt: BookedAppointment) => {
    setBookedList((prev) => [...prev, appt]);
  }, []);

  return (
    <div
      className="pc-desk pc-viewport-fill"
      style={
        {
          ...brandTokens,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'var(--topbar-height) 1fr',

          fontFamily: 'var(--font-family)',
        } as CSSProperties
      }
    >
      <TopBar sidebarWidth={collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth} />

      {/* Main content area */}
      <div
        style={{
          padding: 'var(--content-inset)',
          position: 'relative',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Main Card */}
        <div
          style={{
            background: 'transparent',
            borderRadius: 'var(--main-card-radius)',
            boxShadow: 'var(--shadow-paper)',
            height: '100%',
            display: 'flex',
            overflow: 'clip',
          }}
        >
          <div
            style={{
              width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth,
              flexShrink: 0,
              transition: dragging ? 'none' : 'width 200ms ease-out',
              overflow: 'hidden',
            }}
          >
            {collapsed ? (
              <AppointmentsSidebarRail onExpand={toggle} />
            ) : (
              <AppointmentsSidebar
                onCollapse={toggle}
                onBookPatient={() => openBookDrawer()}
                isPinned={isPinned}
                togglePinned={togglePinned}
              />
            )}
          </div>

          <ResizeGutter onDragStart={startDrag} onDoubleClick={resetWidth} />

          {/* Content Area — paper */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              background: '#ffffff',
              borderRadius: '0 var(--main-card-radius) var(--main-card-radius) 0',
            }}
          >
            <Outlet
              context={
                {
                  collapsed,
                  openBookDrawer,
                  onToggleSidebar: toggle,
                } satisfies AppointmentsOutletContext
              }
            />
          </div>
        </div>
      </div>

      {/* Book Appointment Drawer (shared across all appointment views) */}
      <BookAppointmentDrawer
        open={bookOpen}
        onOpenChange={setBookOpen}
        brandTokens={brandTokens}
        prefill={bookPrefill}
        onBooked={handleBooked}
      />
    </div>
  );
}
